import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import type { AxiosResponse } from 'axios'
import type { GraphQLError, GraphQLFormattedError } from 'graphql'
import type { TFunction } from 'i18next'
import { kebabCase } from 'lodash-es'
import { toast } from 'react-toastify'
import type { Promisable } from 'type-fest'

export class ApiError extends Error {
  errors?: ReadonlyArray<GraphQLError | GraphQLFormattedError>
  status?: number
  res?: AxiosResponse
}

export class CaptchaError extends Error {}

function errorIsApiError(error: unknown): error is ApiError {
  // v4: CombinedGraphQLErrors replaces ApolloError for GraphQL errors
  if (CombinedGraphQLErrors.is(error)) {
    return true
  }
  // Check if it's a network/server error
  if (ServerError.is(error)) {
    return false
  }
  return error instanceof ApiError
}

/**
 * Filter out known errors, such as `ApiError`
 * @param error
 * @returns
 */
export function errorIsUnknownError(error: unknown) {
  if (errorIsApiError(error)) return false
  if (error instanceof CaptchaError) return false
  return true
}

export function throwError(message: string): never {
  throw new Error(message)
}

export function shouldNotHappen(msg?: string): never {
  throw new Error('This should not happen' + (msg ? `: ${msg}` : ''))
}

export function missingArgumentError(name: string): never {
  throw new Error(`Missing required argument: ${name}`)
}

export function getErrorMessage(
  t: TFunction,
  args: {
    errName: string
    /**
     * `artwork:` | `market:model.` | `manga:`
     */
    i18nKeyPrefix: string
  },
) {
  const { errName, i18nKeyPrefix } = args
  return t(`${i18nKeyPrefix}errors.${kebabCase(errName).toLowerCase()}`)
}

export async function toastOnError<Args extends unknown[], Ret>(
  fn: (...args: Args) => Promisable<Ret>,
) {
  return async (...args: Args) => {
    try {
      return await fn(...args)
    } catch (err: any) {
      const message = err.message ?? err
      if (message) {
        toast.error(message)
      }
    }
  }
}

/**
 * This is likely caused by a loader or component continuously redirecting.
 * Check your route loaders and <Navigate> components for circular redirects.
 */
export class RedirectLoopError extends Error {
  pathname: string

  constructor(pathname: string) {
    super(`Router redirect limit exceeded for "${pathname}".`)
    this.name = 'RedirectLoopError'
    this.pathname = pathname
  }
}
